// src/pages/EmployeeList.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaPowerOff,
  FaUserShield,
  FaWallet,
  FaHandHoldingUsd,
} from "react-icons/fa";
import { FaListAlt } from "react-icons/fa";
import { TbArrowsSort } from "react-icons/tb";
import { FiRotateCcw } from "react-icons/fi";
import ConfirmDialog from "../components/ConfirmDialog";
import toast from "react-hot-toast";
import Select from "react-select";

import {
  useGetAllEmployeesQuery,
  useDeleteEmployeeMutation,
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useWithdrawEmployeeCommissionMutation,
  // ✅ NEW (ledger hooks used inside LedgerModal, keep imports)
  useGetEmployeeCommissionLedgerQuery,
  useListCommissionLedgersQuery,
} from "../toolkitServices/employeeApi";

import LedgerModal from "../components/Employee/LedgerModal";
import { useSelector } from "react-redux";

/* ------------ Tiny spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

function useDebouncedValue(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const headerCell =
  "px-3 sm:px-4 py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-3 sm:px-4 py-1.5 text-xs sm:text-sm";

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base, minHeight: 32, height: 32, borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base, padding: "6px 8px",
    backgroundColor: state.isSelected ? "#9481ce" : state.isFocused ? "#ede9fe" : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

/* ------------- skeleton rows ------------- */
const SkeletonRows = ({ cols = 8, rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={dataCell}>
            <div className="h-3 w-20 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/* ---------------- Commission chip ---------------- */
const fmtINR = (n) => {
  const v = Number(n ?? 0) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
     minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const getCommissionValue = (item) =>
  Number(
    item?.employeeCommission ??
      item?.commissionWallet ??
      item?.commissionAmount ??
      0
  ) || 0;

const CommissionChip = ({ value }) => {
  const v = Number(value ?? 0) || 0;
  const isZero = v <= 0;

  return (
    <div className="inline-flex items-center">
      <span
        className={[
          "inline-flex items-center gap-2 px-2.5 py-1 rounded-full border shadow-sm",
          "w-[120px] justify-between",
          isZero
            ? "bg-slate-50 text-slate-500 border-slate-200"
            : "bg-gradient-to-r from-emerald-50 via-violet-50 to-amber-50 text-[#3f2f86] border-violet-200",
        ].join(" ")}
        title="Commission wallet amount"
      >
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <FaWallet className={isZero ? "text-slate-400" : "text-violet-700"} />
        </span>

        <span className="tabular-nums text-[10px] font-semibold">
          {fmtINR(v)}
        </span>
      </span>
    </div>
  );
};

/* ---------------- Withdraw modal ---------------- */
const WithdrawModal = ({
  open,
  onClose,
  employeeName = "",
  maxAmount = 0,
  onSubmit,
}) => {
  const inputRef = useRef(null);

  const [mode, setMode] = useState("full");
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("full");
    setAmount("");
    setErr("");
    const t = setTimeout(() => inputRef.current?.focus?.(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (mode === "full") {
      setAmount(String(maxAmount || 0));
      setErr("");
    } else {
      setAmount("");
      setErr("");
    }
  }, [mode, maxAmount, open]);

  const clampToMax = (raw) => {
    const onlyDigits = String(raw ?? "").replace(/[^\d]/g, "");
    const n = Number(onlyDigits || 0);
    const clamped = Math.min(Math.max(n, 0), Number(maxAmount || 0));
    return { str: onlyDigits === "" ? "" : String(clamped), num: clamped };
  };

  const onAmountChange = (e) => {
    const { str, num } = clampToMax(e.target.value);
    setAmount(str);
    if (num > Number(maxAmount || 0)) setErr("Amount cannot exceed wallet.");
    else setErr("");
  };

  const handleSubmit = () => {
    const max = Number(maxAmount || 0);
    const val = mode === "full" ? max : Number(amount || 0);

    if (mode === "partial") {
      if (!amount) return setErr("Enter amount.");
      if (val <= 0) return setErr("Amount must be greater than 0.");
      if (val > max) return setErr("Amount cannot exceed wallet.");
    } else {
      if (max <= 0) return setErr("Wallet is empty.");
    }

    setErr("");
    onSubmit?.(val);
  };

  const blockKeys = (e) => {
    if (["e", "E", "+", "-", ".", ",", " "].includes(e.key)) e.preventDefault();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ scale: 0.96, y: 10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 10, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-3 overflow-hidden border"
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-800">
                  Withdraw Commission
                </h3>
                <p className="text-[11px] text-slate-500 truncate">
                  {employeeName ? `Employee: ${employeeName}` : ""}{" "}
                  {employeeName ? "• " : ""}
                  Wallet:{" "}
                  <span className="font-semibold">{fmtINR(maxAmount)}</span>
                </p>
              </div>
              <button
                className="text-slate-500 hover:text-slate-700"
                onClick={onClose}
                title="Close"
              >
                <FaTimesCircle />
              </button>
            </div>

            <div className="p-4 space-y-3 text-[11px]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMode("full")}
                  className={[
                    "px-3 py-1.5 rounded-full border text-[11px] font-semibold",
                    mode === "full"
                      ? "bg-violet-600 border-violet-600 text-white"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  Full withdraw
                </button>

                <button
                  type="button"
                  onClick={() => setMode("partial")}
                  className={[
                    "px-3 py-1.5 rounded-full border text-[11px] font-semibold",
                    mode === "partial"
                      ? "bg-violet-600 border-violet-600 text-white"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  Partial
                </button>
              </div>

              <div>
                <label className="block text-slate-600 mb-1">
                  Amount to withdraw
                </label>

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">
                    ₹
                  </span>

                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={mode === "full"}
                    value={mode === "full" ? String(maxAmount || 0) : amount}
                    onChange={onAmountChange}
                    onKeyDown={blockKeys}
                    onPaste={(e) => {
                      e.preventDefault();
                      const text = (
                        e.clipboardData || window.clipboardData
                      ).getData("text");
                      const { str } = clampToMax(text);
                      setAmount(str);
                    }}
                    placeholder="Enter amount"
                    className={[
                      "w-full pl-8 pr-3 py-2 rounded-lg border text-[12px]",
                      "focus:outline-none focus:ring-1 focus:ring-violet-400",
                      mode === "full"
                        ? "bg-slate-50 text-slate-600 border-slate-200"
                        : "bg-white text-slate-800 border-slate-200",
                    ].join(" ")}
                  />
                </div>

                <div className="mt-1 flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">
                    Max: {fmtINR(maxAmount)}
                  </span>

                  {err ? (
                    <span className="text-[10px] text-rose-600">{err}</span>
                  ) : null}
                </div>
              </div>

              {Number(maxAmount || 0) <= 0 ? (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Wallet amount is 0. Withdraw not available.
                </div>
              ) : null}
            </div>

            <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={Number(maxAmount || 0) <= 0}
                className="px-3 py-1.5 rounded-md bg-violet-600 text-white text-[11px] font-semibold hover:bg-violet-700 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

/* ---------------- Ledger Modal helper ---------------- */
const fmtDT = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN");
};

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};


const EmployeeList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [filters, setFilters] = useState({
    department: "",
    branch: "",
    status: "",
  });

  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);
  const deptInputRef = useRef(null);

  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // ✅ withdraw
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawEmployee, setWithdrawEmployee] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(0);

  const [withdrawCommission, { isLoading: isWithdrawing }] =
    useWithdrawEmployeeCommissionMutation();

  // ✅ ledger modal states
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerMode, setLedgerMode] = useState("all"); // "all" | "employee"
  const [ledgerEmployee, setLedgerEmployee] = useState(null);

  // ✅ delete confirm states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ✅ focus mgmt (delete cancel/success)
  const searchInputRef = useRef(null);
  const deleteBtnRefs = useRef(new Map());
  const lastDeleteBtnIdRef = useRef(null);

  useEffect(() => {
    if (!showFilters) return;
    const t = setTimeout(() => {
      if (
        deptInputRef.current &&
        typeof deptInputRef.current.focus === "function"
      ) {
        deptInputRef.current.focus();
      }
    }, 80);
    return () => clearTimeout(t);
  }, [showFilters]);

  const activeBool = useMemo(() => {
    if (filters.status === "Active") return true;
    if (filters.status === "Inactive") return false;
    return undefined;
  }, [filters.status]);

  const qParams = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || "",
      department: filters.department || undefined,
      branch: filters.branch || undefined,
      active: activeBool,
    }),
    [
      page,
      limit,
      debouncedSearch,
      filters.department,
      filters.branch,
      activeBool,
    ]
  );

  const { data, isLoading, isFetching, error } =
    useGetAllEmployeesQuery(qParams);

  const [deleteEmployee, { isLoading: isDeleting }] =
    useDeleteEmployeeMutation();
  const [activateEmp] = useActivateEmployeeMutation();
  const [deactivateEmp] = useDeactivateEmployeeMutation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/members/employee-list/create-employee");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  const rows = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  /* ---------------- Delete flow (ConfirmDialog) ---------------- */
  const openDeleteConfirm = (emp) => {
    lastDeleteBtnIdRef.current = emp?._id || null;
    setDeleteTarget(emp);
    setDeleteConfirmOpen(true);
  };

  const onConfirmDelete = async () => {
    const emp = deleteTarget;
    if (!emp?._id) return;

    // focus target after delete: upper row, else next row, else search
    const idx = sortedRows.findIndex((r) => String(r?._id) === String(emp._id));
    const focusCandidate =
      idx > 0 ? sortedRows[idx - 1] : sortedRows[idx + 1] || null;
    const focusId = focusCandidate?._id || null;

    try {
      await deleteEmployee(emp._id).unwrap();
      toast.success("Employee deleted", { position: "top-center" });

      setDeleteConfirmOpen(false);
      setDeleteTarget(null);

      requestAnimationFrame(() => {
        const el = focusId ? deleteBtnRefs.current.get(focusId) : null;
        if (el) el.focus();
        else searchInputRef.current?.focus();
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.data?.msg || e?.data?.message || "Delete failed", {
        position: "top-center",
      });

      requestAnimationFrame(() => {
        const id = lastDeleteBtnIdRef.current;
        const el = id ? deleteBtnRefs.current.get(id) : null;
        if (el) el.focus();
        else searchInputRef.current?.focus();
      });
    }
  };

  const onToggleActive = async (row) => {
    try {
      if (row.isActive) await deactivateEmp(row._id).unwrap();
      else await activateEmp(row._id).unwrap();
    } catch (e) {
      console.error(e);
      toast.error(e?.data?.msg || "Action failed", { position: "top-center" });
    }
  };

  const chips = useMemo(() => {
    const list = [];
    if (filters.department)
      list.push({
        key: "department",
        label: `Department: ${filters.department}`,
      });
    if (filters.branch)
      list.push({ key: "branch", label: `Branch: ${filters.branch}` });
    if (filters.status)
      list.push({ key: "status", label: `Status: ${filters.status}` });
    if (search) list.push({ key: "search", label: `Search: ${search}` });
    return list;
  }, [filters, search]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    else setFilters((f) => ({ ...f, [key]: "" }));
    setPage(1);
  };

  const clearAllChips = () => {
    setSearch("");
    setFilters({ department: "", branch: "", status: "" });
    setPage(1);
  };

  const onSort = (field) => {
    if (sortBy === field) setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortOrder === "asc" ? 1 : -1;

    const getVal = (row) => {
      switch (sortBy) {
        case "department":
          return (row?.department?.departmentName || "").toLowerCase();
        case "branch":
          return (row?.branch?.branchName || "").toLowerCase();
        case "status":
          return row?.isActive ? "active" : "inactive";
        case "commission":
          return getCommissionValue(row);
        case "name":
        default:
          return `${row?.firstName || ""} ${row?.lastName || ""}`
            .trim()
            .toLowerCase();
      }
    };

    arr.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);

      if (sortBy === "commission") return (av - bv) * dir;
      if (typeof av === "string" && typeof bv === "string")
        return av.localeCompare(bv) * dir;
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });

    return arr;
  }, [rows, sortBy, sortOrder]);

  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [debouncedSearch, filters, sortBy, sortOrder, limit]);

  /* ---------------- Withdraw flow ---------------- */
  const openWithdraw = (emp) => {
    setWithdrawEmployee(emp);
    setWithdrawOpen(true);
  };

  const onWithdrawModalSubmit = (amt) => {
    setWithdrawAmount(Number(amt || 0));
    setWithdrawOpen(false);
    setConfirmOpen(true);
  };

  const onConfirmWithdraw = async () => {
    const emp = withdrawEmployee;
    const amt = Number(withdrawAmount || 0);

    if (!emp?._id) return;
    if (!(amt > 0)) return;

    const walletNow = getCommissionValue(emp);
    if (amt > walletNow) {
      toast.error("Amount cannot exceed wallet.", { position: "top-center" });
      return;
    }

    try {
      await withdrawCommission({
        id: emp._id,
        amount: amt,
        full: amt === walletNow,
        reference: "EmployeeList Withdraw",
        notes: "",
      }).unwrap();

      toast.success("Commission withdrawn successfully", {
        position: "top-center",
      });

      setConfirmOpen(false);
      setWithdrawEmployee(null);
      setWithdrawAmount(0);
    } catch (e) {
      console.error(e);
      toast.error(e?.data?.message || e?.data?.msg || "Withdraw failed", {
        position: "top-center",
      });
    }
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_18px_50px_-24px_rgba(71,55,128,0.45)] md:h-[calc(100vh-70px)]">
      {isFetching && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading employees…</span>
          </div>
        </div>
      )}

      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaUserShield className="text-sm" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">Employee List</h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">Team profiles, access and commission management</p>
          </div>
          {isFetching && rows.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 lg:inline-flex">
            {total || 0} employee{total === 1 ? "" : "s"}
          </span>
          <Link
            to="/members/employee-list/create-employee"
            className="inline-flex h-9 items-center gap-1 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full sm:px-4"
          >
            + Add New
          </Link>
        </div>
      </div>

      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="min-w-0 flex-1">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Name, contact, role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none transition focus:border-violet-300 focus:ring-1 focus:ring-violet-200"
            />
          </div>

          <button
            onClick={() => {
              setLedgerMode("all");
              setLedgerEmployee(null);
              setLedgerOpen(true);
            }}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-violet-200 bg-white px-3 text-[11px] font-medium text-[#6b59c6] transition hover:bg-violet-50"
            title="View all employees commission ledger"
          >
            <FaListAlt /> <span className="hidden sm:inline">All Ledger</span>
          </button>

          <button
            onClick={() => {
              setTempFilters(filters);
              setShowFilters(true);
            }}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-violet-200 bg-white px-3 text-[11px] font-medium text-[#6b59c6] outline-[#6b59c6] transition hover:bg-violet-50"
            title="Advanced filters"
          >
            <FaFilter /> <span className="hidden sm:inline">Filters</span>
          </button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm"
            >
              {c.label}
              <button
                className="text-slate-500 hover:text-red-500"
                onClick={() => clearChip(c.key)}
                title="Clear"
              >
                <FaTimesCircle />
              </button>
            </span>
          ))}
          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      <div
        className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]"
        aria-busy={isFetching}
      >
        {error ? (
          <div className="py-10 text-center text-red-500 text-sm">
            Failed to load employees
          </div>
        ) : (
          <table className="w-full min-w-[1450px] text-left text-xs sm:text-sm">
            <thead className="sticky top-0 z-40 bg-[#efeafd] text-[#4b3d91] shadow-sm">
              <tr>
                <th className={headerCell}>#</th>
                <th className={headerCell}>Employee {sortIcon("name")}</th>
                <th className={headerCell}>Emp Code</th>
                <th className={headerCell}>Emp Mail</th>
                <th className={headerCell}>
                  Department {sortIcon("department")}
                </th>
                <th className={headerCell}>Branch {sortIcon("branch")}</th>
                <th className={headerCell}>Counter</th>
                <th className={headerCell}>Contact</th>
                <th className={headerCell}>Role</th>
                <th className={headerCell}>
                  Commission Wallet {sortIcon("commission")}
                </th>
                <th className={headerCell}>Status {sortIcon("status")}</th>
                <th className={`${headerCell} sticky right-0 top-0 z-50 w-[72px] min-w-[72px] border-l border-violet-200 bg-[#ddd5f7] !px-1 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[156px] sm:min-w-[156px] sm:!px-2`}><span className="sm:hidden">Act</span><span className="hidden sm:inline">Action</span></th>
              </tr>
            </thead>

            <tbody className="bg-white text-gray-700">
              <AnimatePresence initial={false}>
                {isFetching && rows.length ? (
                  <SkeletonRows cols={10} rows={8} />
                ) : sortedRows.length > 0 ? (
                  sortedRows.map((item, index) => {
                    const commission = getCommissionValue(item);

                    return (
                      <motion.tr
                        key={item._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        className="group border-t border-slate-100 transition hover:bg-[#f7f5ff]"
                      >
                        <td className={dataCell}>
                          {(page - 1) * limit + index + 1}
                        </td>

                        <td className={dataCell}>
                          {`${item?.firstName || ""} ${
                            item?.lastName || ""
                          }`.trim() || "—"}
                        </td>

                        <td className={dataCell}>
                          {item?.employeeCode || "—"}
                        </td>
                        <td className={dataCell}>
                          {item?.officialEmail || "—"}
                        </td>

                        <td className={dataCell}>
                          {item?.department?.departmentName || "—"}
                        </td>

                        <td className={dataCell}>
                          {item?.branch?.branchName || "—"}
                        </td>

                        <td className={dataCell}>
                          {item?.counter?.counterName || "—"}
                        </td>

                        <td className={dataCell}>{item?.contact || "—"}</td>

                        <td className={dataCell}>
                          {item?.role?.roleName || "—"}
                        </td>

                        <td className={dataCell}>
                          <div className="flex items-center gap-2">
                            <CommissionChip value={commission} />

                            <button
                              type="button"
                              disabled={commission <= 0 || isWithdrawing}
                              onClick={() => openWithdraw(item)}
                              className={[
                                "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border shadow-sm",
                                commission > 0 && !isWithdrawing
                                  ? "bg-white border-violet-200 text-violet-700 hover:bg-violet-50"
                                  : "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed",
                              ].join(" ")}
                              title={
                                commission > 0
                                  ? "Withdraw commission"
                                  : "No commission to withdraw"
                              }
                            >
                              <FaHandHoldingUsd className="text-[12px]" />
                              {isWithdrawing ? "Withdrawing…" : "Withdraw"}
                            </button>
                          </div>
                        </td>

                        <td className={dataCell}>
                          <span
                            className={[
                              "text-[11px] px-2 py-0.5 rounded-full",
                              item.isActive
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-rose-100 text-rose-700",
                            ].join(" ")}
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td
                          className={`${dataCell} sticky right-0 z-10 w-[72px] min-w-[72px] border-l border-violet-100 bg-violet-50 !px-1 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[156px] sm:min-w-[156px] sm:!px-2`}
                        >
                          <div className="mx-auto grid w-fit grid-cols-3 items-center justify-center gap-0.5 sm:inline-flex sm:gap-1">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="inline-flex h-5 w-5 items-center justify-center rounded-md text-[9px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              onClick={() =>
                                navigate(
                                  `/members/employee-list/${item._id}/edit`
                                )
                              }
                              title="Edit employee"
                            >
                              <FaPencilAlt />
                            </motion.button>

                            {/* ✅ Delete uses ConfirmDialog + focus mgmt */}
                            <motion.button
                              ref={(el) => {
                                if (!item?._id) return;
                                if (el) deleteBtnRefs.current.set(item._id, el);
                                else deleteBtnRefs.current.delete(item._id);
                              }}
                              type="button"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="order-2 inline-flex h-5 w-5 items-center justify-center rounded-md text-[9px] text-rose-500 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-300 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              onClick={() => {
                               if(canDelete(user)){
                                 openDeleteConfirm(item)
                               }else{
                                 toast.error(
                                                                  "You don't have permission to delete",
                                                                );
                               }
                              }}
                              disabled={isDeleting}
                              title="Delete employee"
                            >
                              <FaTrashAlt />
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className={`order-1 inline-flex h-5 w-5 items-center justify-center rounded-md border text-[9px] sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px] ${
                                item.isActive
                                  ? "border-amber-100 text-amber-600 hover:bg-amber-50"
                                  : "border-emerald-100 text-emerald-600 hover:bg-emerald-50"
                              }`}
                              onClick={() => onToggleActive(item)}
                              title={
                                item.isActive
                                  ? "Deactivate employee"
                                  : "Activate employee"
                              }
                            >
                              <FaPowerOff />
                            </motion.button>

                            {/* ✅ Ledger button */}
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="order-3 inline-flex h-5 w-5 items-center justify-center rounded-md border border-slate-200 bg-white text-[9px] text-slate-700 hover:bg-slate-100 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              onClick={() => {
                                setLedgerMode("employee");
                                setLedgerEmployee(item);
                                setLedgerOpen(true);
                              }}
                              title="View commission ledger"
                            >
                              <FaListAlt />
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="order-4 inline-flex h-5 w-5 items-center justify-center rounded-md border border-violet-200 bg-violet-50 text-[9px] text-violet-700 hover:bg-violet-100 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              onClick={() =>
                                navigate(
                                  `/user-management/special-permissions/${item._id}`
                                )
                              }
                              title="Special permissions"
                            >
                              <FaUserShield />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })
                ) : (
                  !isLoading &&
                  !isFetching && (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td
                        colSpan={10}
                        className="px-4 py-8 text-center text-xs text-slate-500"
                      >
                        No matching employee records found. Try changing filters
                        or search text.
                      </td>
                    </motion.tr>
                  )
                )}
              </AnimatePresence>
            </tbody>
          </table>
        )}
      </div>

      <div className="shrink-0 flex items-center justify-between gap-2 border-t bg-slate-50/80 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
        <div className="hidden text-slate-600 sm:block">
            Showing{" "}
            <span className="font-semibold">
              {sortedRows.length ? (page - 1) * limit + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {sortedRows.length ? (page - 1) * limit + sortedRows.length : 0}
            </span>{" "}
            of <span className="font-semibold">{total}</span> employees
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({ value: number, label: String(number) }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button
              type="button"
              disabled={page <= 1 || isFetching || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base shadow-sm disabled:opacity-40 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              aria-label="Previous page"
            >
              <span className="sm:hidden">&lsaquo;</span><span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{ value: pages ? page : 1, label: String(pages ? page : 1) }}
                options={Array.from({ length: Math.max(1, pages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, pages)}</span>
            </span>
            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select value={{ value: pages ? page : 1, label: String(pages ? page : 1) }} options={Array.from({ length: Math.max(1, pages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))} onChange={(option) => setPage(Number(option?.value) || 1)} isDisabled={isFetching || isLoading} isSearchable={false} menuPlacement="top" menuPosition="fixed" menuPortalTarget={document.body} menuShouldScrollIntoView={false} styles={paginationSelectStyles} className="w-[58px]" aria-label="Select page number" />
              <span>of {Math.max(1, pages)}</span>
            </span>

            <button
              type="button"
              disabled={page >= pages || isFetching || isLoading}
              onClick={() =>
                setPage((p) => (pages ? Math.min(pages, p + 1) : p + 1))
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base shadow-sm disabled:opacity-40 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              aria-label="Next page"
            >
              <span className="sm:hidden">&rsaquo;</span><span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* FILTER MODAL */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowFilters(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">
                    Employee filters
                  </h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Filter employees by department, branch and status.
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setShowFilters(false)}
                  aria-label="Close filters"
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-3 text-[11px]">
                <div>
                  <label className="block text-slate-600 mb-1">
                    Department
                  </label>
                  <input
                    ref={deptInputRef}
                    type="text"
                    value={tempFilters.department}
                    onChange={(e) =>
                      setTempFilters((f) => ({
                        ...f,
                        department: e.target.value,
                      }))
                    }
                    placeholder="e.g., Sales, Karigar…"
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-[11px] outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 mb-1">Branch</label>
                  <input
                    type="text"
                    value={tempFilters.branch}
                    onChange={(e) =>
                      setTempFilters((f) => ({
                        ...f,
                        branch: e.target.value,
                      }))
                    }
                    placeholder="e.g., Head Office…"
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-[11px] outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 mb-1">Status</label>
                  <select
                    value={tempFilters.status}
                    onChange={(e) =>
                      setTempFilters((f) => ({
                        ...f,
                        status: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                  >
                    <option value="">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() =>
                    setTempFilters({ department: "", branch: "", status: "" })
                  }
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowFilters(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters(tempFilters);
                      setPage(1);
                      setShowFilters(false);
                    }}
                    className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae]"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Withdraw Modal */}
      <WithdrawModal
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        employeeName={
          withdrawEmployee
            ? `${withdrawEmployee?.firstName || ""} ${
                withdrawEmployee?.lastName || ""
              }`.trim()
            : ""
        }
        maxAmount={withdrawEmployee ? getCommissionValue(withdrawEmployee) : 0}
        onSubmit={onWithdrawModalSubmit}
      />

      {/* Withdraw Confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => {
          if (!isWithdrawing) setConfirmOpen(false);
        }}
        onConfirm={onConfirmWithdraw}
        loading={isWithdrawing}
        destructive={false}
        title="Confirm withdrawal?"
        message={
          withdrawEmployee
            ? `Withdraw ${fmtINR(withdrawAmount)} from ${
                `${withdrawEmployee?.firstName || ""} ${
                  withdrawEmployee?.lastName || ""
                }`.trim() || "employee"
              } commission wallet?`
            : `Withdraw ${fmtINR(withdrawAmount)}?`
        }
        confirmText="Yes, withdraw"
        cancelText="Cancel"
        icon={
          <div className="h-10 w-10 rounded-full grid place-items-center bg-violet-100 text-violet-700">
            <FaHandHoldingUsd />
          </div>
        }
      />

      {/* ✅ Delete Confirmation (focus returns to same delete icon on cancel) */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteConfirmOpen(false);
            setDeleteTarget(null);

            requestAnimationFrame(() => {
              const id = lastDeleteBtnIdRef.current;
              const el = id ? deleteBtnRefs.current.get(id) : null;
              if (el) el.focus();
              else searchInputRef.current?.focus();
            });
          }
        }}
        onConfirm={onConfirmDelete}
        loading={isDeleting}
        destructive={true}
        title="Delete employee?"
        message={
          deleteTarget
            ? `Are you sure you want to delete ${
                `${deleteTarget?.firstName || ""} ${
                  deleteTarget?.lastName || ""
                }`.trim() || "this employee"
              } permanently? This action cannot be undone.`
            : "Are you sure you want to delete this employee permanently?"
        }
        confirmText="Yes, delete"
        cancelText="Cancel"
        icon={
          <div className="h-10 w-10 rounded-full grid place-items-center bg-rose-100 text-rose-700">
            <FaTrashAlt />
          </div>
        }
      />

      {/* ✅ Ledger Modal */}
      <LedgerModal
        open={ledgerOpen}
        onClose={() => setLedgerOpen(false)}
        mode={ledgerMode}
        employee={ledgerEmployee}
      />
    </div>
  );
};

export default EmployeeList;
